import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { assertNever } from '@/utils/assertNever'
import { erc4626Abi, etherUnits, formatUnits, parseUnits } from 'viem'
import { Config } from 'wagmi'
import { multicall, readContract } from 'wagmi/actions'
import { TokenConfig } from './types'
import { pythOracleAbi } from '../../../../abis/evm/pythOracle'

export interface CreateOraclePriceFetcherParams {
  tokenConfig: TokenConfig
  wagmiConfig: Config
  chainId: number
}

export function createOraclePriceFetcher({
  tokenConfig,
  wagmiConfig,
  chainId,
}: CreateOraclePriceFetcherParams): () => Promise<NormalizedUnitNumber> {
  switch (tokenConfig.oracleType) {
    case 'zero-price':
      return async () => NormalizedUnitNumber(0)

    case 'fixed-usd':
      return async () => NormalizedUnitNumber(1)

    case 'vault':
      return async () => {
        const result = await readContract(wagmiConfig, {
          abi: erc4626Abi,
          address: tokenConfig.address,
          functionName: 'convertToAssets',
          args: [parseUnits('1', etherUnits.wei)],
          chainId,
        })

        return NormalizedUnitNumber(formatUnits(result, etherUnits.wei))
      }

    case 'pyth-oracle':
      return async () => {
        if (!tokenConfig.oracleAddress) {
          throw new Error('Oracle address is required for pyth-oracle')
        }

        const [price, decimals] = await multicall(wagmiConfig, {
          chainId,
          allowFailure: false,
          contracts: [
            {
              abi: pythOracleAbi,
              address: tokenConfig.oracleAddress,
              functionName: 'latestAnswer',
            },
            {
              abi: pythOracleAbi,
              address: tokenConfig.oracleAddress,
              functionName: 'decimals',
            },
          ],
        })

        return NormalizedUnitNumber(formatUnits(price, decimals))
      }

    default:
      assertNever(tokenConfig.oracleType)
  }
}
